const PAID_PLANS = new Set(["pro", "elite"]);

export function adaptEntitlement(payload, { previous, ownerUid } = {}) {
  const status = String(payload?.status || (payload?.entitlement ? "ready" : "unknown"));
  const entitlement = payload?.entitlement || null;
  const degraded = status === "degraded";

  if (!entitlement && previous && previous.ownerUid === ownerUid) {
    return {
      ...previous,
      freshness: "stale",
      status: "unknown",
      canCheckout: false,
    };
  }

  if (!entitlement) {
    return {
      ownerUid,
      status: "unknown",
      freshness: "unknown",
      plan: null,
      planLabel: null,
      accessState: null,
      source: null,
      willRenew: null,
      currentPeriodEnd: null,
      graceEndsAt: null,
      manageTarget: "none",
      limits: null,
      effectiveLimits: null,
      capabilities: null,
      canCheckout: false,
      headline: "We couldn’t confirm your subscription.",
      detail: "Try again in a moment. This is not a confirmed Free plan.",
    };
  }

  if (degraded) {
    return {
      ownerUid,
      status: "degraded",
      freshness: "degraded",
      plan: null,
      planLabel: null,
      accessState: null,
      source: entitlement.source || null,
      willRenew: null,
      currentPeriodEnd: null,
      graceEndsAt: null,
      manageTarget: "none",
      limits: null,
      effectiveLimits: null,
      capabilities: null,
      canCheckout: false,
      headline: "We couldn’t confirm your subscription.",
      detail: "Your access was not verified. We will not invite a new purchase until this is resolved.",
      correlationId: payload?.correlationId || null,
    };
  }

  const plan = String(entitlement.plan || "free").toLowerCase();
  const accessState = String(entitlement.accessState || "inactive").toLowerCase();
  const source = String(entitlement.source || "none").toLowerCase();
  const manageTarget = String(entitlement.manageTarget || "none");
  const paid = PAID_PLANS.has(plan);
  const activeLike = accessState === "active" || accessState === "grace";
  const planLabel =
    plan === "pro" ? "Stratify Pro" : plan === "elite" ? "Stratify Elite" : "Free";

  let headline = `${planLabel} account`;
  let detail = "No paid subscription.";
  if (degraded || status === "unknown") {
    headline = "We couldn’t confirm your subscription.";
    detail = "Try again shortly.";
  } else if (accessState === "manual_review" || source === "multiple") {
    headline = "Your subscription needs review";
    detail = "Contact support before making billing changes.";
  } else if (accessState === "grace") {
    headline = `${planLabel} — payment needs attention`;
    detail = entitlement.graceEndsAt
      ? `Grace continues until ${formatDate(entitlement.graceEndsAt)}.`
      : "Update billing to keep access.";
  } else if (paid && accessState === "active" && entitlement.willRenew === false) {
    headline = `${planLabel} — renewal is off`;
    detail = entitlement.currentPeriodEnd
      ? `Your access continues until ${formatDate(entitlement.currentPeriodEnd)}.`
      : "Your access continues until the end of the paid period.";
  } else if (paid && activeLike && entitlement.willRenew) {
    headline = `${planLabel} is active`;
    detail = entitlement.currentPeriodEnd
      ? `Renews on ${formatDate(entitlement.currentPeriodEnd)}.`
      : "Your paid access is active.";
  } else if (paid && accessState === "inactive") {
    headline = `${planLabel} access is inactive`;
    detail = "Manage billing through the original purchase provider.";
  }

  const unknownStatus = status === "unknown" || status === "degraded" || accessState === "manual_review";
  const appleManaged = manageTarget === "apple_subscriptions" || source === "app_store";

  return {
    ownerUid,
    status,
    freshness: "ready",
    plan,
    planLabel,
    accessState,
    source,
    willRenew: entitlement.willRenew === true,
    currentPeriodEnd: entitlement.currentPeriodEnd || null,
    graceEndsAt: entitlement.graceEndsAt || null,
    manageTarget,
    limits: entitlement.limits || null,
    effectiveLimits: entitlement.effectiveLimits || entitlement.limits || null,
    capabilities: entitlement.capabilities || null,
    eliteCapacityCertified: entitlement.eliteCapacityCertified,
    reasonCode: entitlement.reasonCode || null,
    correlationId: payload?.correlationId || null,
    uxGates: payload?.uxGates || null,
    canCheckout: !unknownStatus && !paid && !appleManaged && manageTarget !== "support",
    appleManaged,
    headline,
    detail,
    raw: entitlement,
  };
}

export function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}
